import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { District } from '@citizen-walk/shared-types';
import { useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

/**
 * ユーザー管理の地区設定 panel (2026-09-07)。マスター管理者専用。
 *
 * `participantAdmin.districtAdminCanManage` — この地区の地区管理者にもユーザー管理を開放するか
 * (未指定 = false = マスター管理者のみ)。参加者の登録データは個人に紐づくため opt-in とする。
 *
 * 書込保護: firestore.rules の地区管理者 update 許可フィールドに `participantAdmin` を含めていない
 * ため、地区管理者は自分でこの許可を立てられない (DistrictStreetViewPanel と同じ方式)。
 * サーバ側 callable (`updateParticipant` / `issueParticipantRecoveryCode`) も同じ値を見て判定する。
 */

export interface DistrictParticipantAdminPanelProps {
  district: District;
  uid: string;
  canEdit: boolean;
}

export function DistrictParticipantAdminPanel({
  district,
  uid,
  canEdit,
}: DistrictParticipantAdminPanelProps) {
  const { toast } = useToast();
  const [districtAdminCanManage, setDistrictAdminCanManage] = useState<boolean>(
    district.participantAdmin?.districtAdminCanManage === true,
  );
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!canEdit) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'districts', district.id),
        {
          participantAdmin: { districtAdminCanManage },
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast('success', 'ユーザー管理の設定を保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      padding="lg"
      className="border-primary-200"
      data-testid="district-participant-admin-panel"
    >
      <h2 className="text-base font-bold text-slate-900 mb-2">
        👥 ユーザー管理をだれに許可するか（マスター管理者のみ設定できます）
      </h2>
      <p className="text-xs text-slate-500 mb-3">
        参加者の登録データは個人に関わる情報のため、既定ではマスター管理者だけが編集できます。
        地区の担当者に日々の修正を任せる場合は、ここで許可してください。
      </p>
      <label className="flex items-start gap-2 mb-3">
        <input
          type="checkbox"
          checked={districtAdminCanManage}
          onChange={(e) => setDistrictAdminCanManage(e.target.checked)}
          disabled={!canEdit || saving}
          className="h-4 w-4 mt-0.5"
          data-testid="participant-admin-district-can-manage"
        />
        <span className="text-sm">
          この地区の地区管理者にもユーザー管理を許可する
          <span className="block text-xs text-slate-500">
            オフのときは、地区管理者のメニューにユーザー管理が出ません（編集もできません）。
          </span>
        </span>
      </label>
      <div className="flex justify-end">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving}
          data-testid="participant-admin-save"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </Card>
  );
}
